import { XYPosition } from "@xyflow/react"
import {
    EdgeTypes,
    EdgeSubtypes,
    type ApiGraphData,
    type ApiNode,
    type ApiEdge,
    type NetworkNode,
    type NetworkEdge,
    type NetworkNodeData,
    type NetworkEdgeData
} from "../types"
import { transformMetadataForApi } from "./utils"

/** Transform NetworkNodeData to API format */
export const toApiNode = (node: NetworkNodeData, position?: XYPosition): ApiNode => ({
    id: `${node.nodeType.toLowerCase()}:${node.identifierKey}`,
    identifier_key: node.identifierKey,
    type: node.nodeType,
    category: node.nodeCategory,
    display_name: node.displayName,
    priority: node.priority,
    position,
    metadata: transformMetadataForApi(node.metadata),
    view_context: node.viewContext
})

/**
 * Transform React Flow nodes and edges back to API format.
 * Filters out disabled and readonly nodes, and edges connected to them.
 */
export function transformReactFlowToApi(nodes: NetworkNode[], edges: NetworkEdge[]): ApiGraphData {
    const activeNodes = nodes.filter((node) => {
        const data = node.data as NetworkNodeData
        return !data.disabled && !data.readonly
    })

    const excludedNodeIds = new Set(
        nodes
            .filter((node) => {
                const data = node.data as NetworkNodeData
                return data.disabled || data.readonly
            })
            .map((node) => node.id)
    )

    const activeEdges = edges.filter((edge) => !excludedNodeIds.has(edge.source) && !excludedNodeIds.has(edge.target))

    // Build mapping from React Flow ID to correct API ID (computed from node data)
    // This handles cases where identifier_key changed but node.id still has the old value
    const nodeIdMapping = new Map<string, string>()

    const apiNodes: ApiNode[] = activeNodes.map((node) => {
        const data = node.data as NetworkNodeData
        const apiNode = toApiNode(data, node.position)
        nodeIdMapping.set(node.id, apiNode.id)
        return apiNode
    })

    const apiEdges: ApiEdge[] = activeEdges.map((edge) => {
        const data = edge.data as NetworkEdgeData
        const sourceId = nodeIdMapping.get(edge.source) ?? edge.source
        const targetId = nodeIdMapping.get(edge.target) ?? edge.target
        const apiEdge: ApiEdge = {
            source_id: sourceId.toLowerCase(),
            target_id: targetId.toLowerCase(),
            type: data?.type || EdgeTypes.INCLUDED,
            sub_type: data?.subtype || EdgeSubtypes.IS_LINKED
        }
        // Include external_id for cross-boundary edges
        if (data?.externalId) {
            apiEdge.external_id = data.externalId
        }
        return apiEdge
    })

    return {
        nodes: apiNodes,
        edges: apiEdges
    }
}
